import { PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Empty, Input, Modal, Pagination, Skeleton, message } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  batchDeleteContentProjects,
  createContentProject,
  deleteContentProject,
  duplicateContentProject,
  listContentProjects,
  setContentProjectVisibility,
  type ContentProjectSummary,
  type ContentProjectTeamBlock,
  type ContentProjectView,
  type SaasApiError,
} from '../../api'
import { syncCanvasNavigationContextFromProject } from '../../navigation/canvasNavigationContext'
import { openCanvasFromWorkspace } from '../canvasWorkspaceTransition'
import VisualProjectCard from './gallery/VisualProjectCard'
import './content-production.css'
import '../../ui/tenant-items.css'

const PAGE_SIZE = 16

export default function ContentProductionListPage() {
  const { t } = useTranslation(['contentProjects', 'common', 'actions'])
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<ContentProjectSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [copyingId, setCopyingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [batchDeleting, setBatchDeleting] = useState(false)
  // TASK-00556 — team scope. `team` comes from the list response itself (no
  // extra request); tabs only render when the Workspace has >= 2 ACTIVE members.
  const [view, setView] = useState<ContentProjectView>('mine')
  const [team, setTeam] = useState<ContentProjectTeamBlock | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const teamEnabled = Boolean(team?.enabled)

  // TASK-00566 — explicit load status, independent of `items`/`team`.
  // Root cause of "返回工作台后内容项目消失": this page used to have no
  // notion of ERROR distinct from "freshly mounted, nothing loaded yet"
  // (both looked like `items.length === 0`) — a transient failure (e.g. a
  // brief backend hiccup right as Canvas unmounts and this page remounts)
  // rendered the exact same "还没有内容项目" empty state a real empty
  // Workspace would show, AND silently hid the 我的项目/团队共享/全部 tabs
  // (`team` was still its just-initialized `null`). `status`/`loadError`
  // let the page render a THIRD, distinguishable state instead of
  // reusing the empty-state branch for failures.
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [loadError, setLoadError] = useState<string | null>(null)
  // Ignore a response that resolves after a newer request has already
  // been fired for this component instance (e.g. rapid tab/page/search
  // changes) — spec requirement "请求竞态不得覆盖当前 Workspace 的正确结果".
  const requestSeqRef = useRef(0)
  // Only clear already-rendered items/team when the request is for a
  // DIFFERENT workspace than the last one we successfully rendered — a
  // transient failed reload of the SAME workspace must never wipe good
  // data that is still on screen (spec requirement "请求失败时保留合理的
  // 页面状态" / "不得将请求失败映射为空项目列表").
  const lastLoadedWorkspaceRef = useRef<string | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [q])

  useEffect(() => {
    setSelectedIds([])
  }, [debouncedQ, page, view])

  const load = useCallback(async () => {
    if (!workspaceId) return
    const seq = ++requestSeqRef.current
    // Switching to a genuinely different Workspace: clear stale data up
    // front so the previous Workspace's projects/tabs never flash while
    // the new one is loading (spec requirement "Workspace 切换时不得展示
    // 其他 Workspace 的旧数据"). A reload of the SAME workspace (retry,
    // search, pagination, tab switch) does not hit this branch, so any
    // already-rendered good data survives a transient failure below.
    if (lastLoadedWorkspaceRef.current !== workspaceId) {
      setItems([])
      setTotal(0)
      setTeam(null)
    }
    setStatus('loading')
    setLoading(true)
    try {
      const { data } = await listContentProjects(workspaceId, {
        q: debouncedQ || undefined,
        page,
        page_size: PAGE_SIZE,
        view,
      })
      if (seq !== requestSeqRef.current) return // superseded by a newer request
      setItems(data.items)
      setTotal(data.total)
      setTeam(data.team ?? null)
      setStatus('success')
      setLoadError(null)
      lastLoadedWorkspaceRef.current = workspaceId
      // Membership shrank to a single member while on a team tab: the server
      // already answered with the personal list, fall back to the personal tab.
      if (data.team && !data.team.enabled && view !== 'mine') setView('mine')
    } catch (e) {
      if (seq !== requestSeqRef.current) return
      const errorMessage = (e as SaasApiError).message || t('contentProjects:toast.loadFailed')
      message.error(errorMessage)
      setStatus('error')
      setLoadError(errorMessage)
      // Deliberately do NOT touch items/total/team here — a transient
      // load failure must never be rendered as "0 projects" (spec
      // requirement "ERROR != SUCCESS_EMPTY").
    } finally {
      if (seq === requestSeqRef.current) setLoading(false)
    }
  }, [workspaceId, debouncedQ, page, view, t])

  const onChangeView = (next: ContentProjectView) => {
    if (next === view) return
    setItems([])
    setTotal(0)
    setPage(1)
    setStatus('idle')
    setView(next)
  }

  const onToggleVisibility = (project: ContentProjectSummary) => {
    if (!workspaceId || togglingId) return
    const next = project.visibility === 'workspace' ? 'private' : 'workspace'
    void (async () => {
      setTogglingId(project.id)
      try {
        const { data } = await setContentProjectVisibility(workspaceId, project.id, next)
        // Only reflect what the server confirmed — never optimistic.
        setItems((prev) =>
          prev.map((p) =>
            p.id === project.id
              ? { ...p, visibility: data.visibility, permissions: data.permissions ?? p.permissions }
              : p,
          ),
        )
        message.success(
          data.visibility === 'workspace'
            ? t('contentProjects:team.share.successShared')
            : t('contentProjects:team.share.successPrivate'),
        )
      } catch (e) {
        message.error((e as SaasApiError).message || t('contentProjects:team.share.failed'))
      } finally {
        setTogglingId(null)
      }
    })()
  }

  useEffect(() => {
    void load()
  }, [load])

  const selectedCount = selectedIds.length
  const selectionActive = selectedCount > 0
  // Only projects the viewer may edit are selectable / batch-deletable.
  const selectableItems = items.filter((item) => item.permissions?.can_edit ?? true)
  const allCurrentSelected =
    selectableItems.length > 0 && selectableItems.every((item) => selectedIds.includes(item.id))

  const toggleSelect = useCallback((projectId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        if (prev.includes(projectId)) return prev
        return [...prev, projectId]
      }
      return prev.filter((id) => id !== projectId)
    })
  }, [])

  const selectAllCurrent = () => {
    setSelectedIds(selectableItems.map((item) => item.id))
  }

  const clearSelection = () => {
    setSelectedIds([])
  }

  const onCreate = async () => {
    if (!workspaceId) return
    setCreating(true)
    try {
      const { data } = await createContentProject(workspaceId, t('contentProjects:unnamedProject'))
      if (!data?.id) {
        throw new Error(t('contentProjects:toast.createMissingId'))
      }
      message.success(t('contentProjects:toast.createSuccess'))
      openCanvasFromWorkspace(navigate, `/workspace/${workspaceId}/content-production/${data.id}/canvas`)
    } catch (e) {
      message.error((e as SaasApiError).message || t('contentProjects:toast.createFailedGeneric'))
    } finally {
      setCreating(false)
    }
  }

  /** `mine` = the card is the signed-in user's own project. A teammate's shared
   * project is opened (read-only) via the same entry but never recorded as the
   * return context (VistoeraTASK-00577); Canvas re-records after load if owned. */
  const openProject = (projectId: string, mine = true) => {
    if (mine) syncCanvasNavigationContextFromProject(workspaceId, projectId, 'content-production')
    openCanvasFromWorkspace(navigate, `/workspace/${workspaceId}/content-production/${projectId}/canvas`)
  }

  const onCopy = (project: ContentProjectSummary) => {
    if (!workspaceId || copyingId) return
    void (async () => {
      setCopyingId(project.id)
      try {
        const { data } = await duplicateContentProject(workspaceId, project.id)
        if (project.is_mine === false && data?.id) {
          // Copying a teammate's shared project: the copy is a private,
          // independent project of mine — open it in the editable Canvas.
          const restricted = data.copy_report?.restricted_asset_count ?? 0
          message.success(
            restricted > 0
              ? t('contentProjects:team.toast.copiedRestricted', { count: restricted })
              : t('contentProjects:team.toast.copiedToMine'),
          )
          openProject(data.id)
          return
        }
        message.success(t('contentProjects:toast.copySuccess'))
        await load()
      } catch (e) {
        message.error((e as SaasApiError).message || t('contentProjects:toast.copyFailedGeneric'))
      } finally {
        setCopyingId(null)
      }
    })()
  }

  const onDelete = async (project: ContentProjectSummary) => {
    if (!workspaceId || deletingId) return
    setDeletingId(project.id)
    try {
      await deleteContentProject(workspaceId, project.id)
      message.success(t('contentProjects:toast.deleteSuccess'))
      setSelectedIds((prev) => prev.filter((id) => id !== project.id))
      if (items.length <= 1 && page > 1) {
        setPage((p) => p - 1)
      } else {
        await load()
      }
    } catch (e) {
      message.error((e as SaasApiError).message || t('contentProjects:toast.deleteFailedGeneric'))
      throw e
    } finally {
      setDeletingId(null)
    }
  }

  const onBatchDelete = () => {
    if (!workspaceId || selectedCount === 0 || batchDeleting) return
    Modal.confirm({
      title: t('contentProjects:batch.deleteConfirmTitle', { count: selectedCount }),
      content: t('contentProjects:batch.deleteConfirmDescription'),
      okText: t('contentProjects:batch.deleteConfirmOk'),
      okType: 'danger',
      cancelText: t('actions:cancel'),
      onOk: async () => {
        setBatchDeleting(true)
        try {
          const { data } = await batchDeleteContentProjects(workspaceId, selectedIds)
          message.success(t('contentProjects:batch.deleteSuccess', { count: data.deleted_count }))
          setSelectedIds([])
          if (items.length <= data.deleted_count && page > 1) {
            setPage((p) => p - 1)
          } else {
            await load()
          }
        } catch (e) {
          message.error((e as SaasApiError).message || t('contentProjects:batch.deleteFailed'))
        } finally {
          setBatchDeleting(false)
        }
      },
    })
  }

  return (
    <div className="tc-items-page cpg-page">
      <div className="tc-page-header">
        <div>
          <h2 className="tc-page-title">{t('contentProjects:title')}</h2>
          <p className="tc-page-desc">{t('contentProjects:subtitle')}</p>
        </div>
        <div className="tc-page-actions">
          <Button type="primary" icon={<PlusOutlined />} loading={creating} onClick={() => void onCreate()}>
            {t('contentProjects:newProject')}
          </Button>
        </div>
      </div>

      <div className="tc-data-card">
        {teamEnabled ? (
          <div
            className="iac-view-tabs"
            role="tablist"
            aria-label={t('contentProjects:team.viewsAria')}
            data-testid="content-projects-view-tabs"
          >
            {(['mine', 'shared', 'all'] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                className={`iac-view-tab${view === v ? ' is-active' : ''}`}
                onClick={() => onChangeView(v)}
                data-testid={`content-projects-view-tab-${v}`}
              >
                {t(`contentProjects:team.views.${v}`)}
              </button>
            ))}
          </div>
        ) : null}

        {status === 'error' ? (
          <Alert
            type="error"
            showIcon
            message={loadError || t('contentProjects:toast.loadFailed')}
            action={
              <Button size="small" danger onClick={() => void load()} data-testid="content-projects-retry">
                {t('actions:retry')}
              </Button>
            }
            style={{ marginBottom: 12 }}
            data-testid="content-projects-load-error-banner"
          />
        ) : null}

        <div className="cpg-list-toolbar">
          {selectionActive ? (
            <div
              className="cpg-batch-bar"
              role="region"
              aria-label={t('contentProjects:batch.barLabel')}
              data-testid="content-projects-batch-bar"
            >
              <span className="cpg-batch-bar-count" data-testid="content-projects-batch-selected-count">
                {t('contentProjects:batch.selectedCount', { count: selectedCount })}
              </span>
              <div className="cpg-batch-bar-actions">
                <Button
                  type="link"
                  size="small"
                  disabled={allCurrentSelected || selectableItems.length === 0}
                  onClick={selectAllCurrent}
                  data-testid="content-projects-batch-select-all"
                >
                  {t('contentProjects:batch.selectAll')}
                </Button>
                <Button
                  type="link"
                  size="small"
                  onClick={clearSelection}
                  data-testid="content-projects-batch-clear-selection"
                >
                  {t('contentProjects:batch.clearSelection')}
                </Button>
                <Button
                  type="primary"
                  danger
                  size="small"
                  loading={batchDeleting}
                  onClick={onBatchDelete}
                  data-testid="content-projects-batch-delete"
                >
                  {t('contentProjects:batch.delete')}
                </Button>
              </div>
            </div>
          ) : null}
          <div className="cpg-list-toolbar-search">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t('contentProjects:searchPlaceholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 240 }}
            />
          </div>
        </div>

        {loading && items.length === 0 ? (
          <div className="cpg-gallery-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="cpg-card cpg-card--skeleton">
                <Skeleton.Image active style={{ width: '100%', height: 180 }} />
                <Skeleton active paragraph={{ rows: 1 }} />
              </div>
            ))}
          </div>
        ) : items.length === 0 && status === 'error' ? (
          // TASK-00566 — a load failure with nothing already on screen is
          // NOT the same as a verified-empty Workspace: never show
          // "还没有内容项目" (which implies the server confirmed zero
          // projects) when the server was never actually asked
          // successfully. The `Alert` above already carries the error
          // message + retry action; this is just a neutral placeholder so
          // the page isn't blank underneath it.
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('contentProjects:emptyState.loadError')}
            style={{ padding: '48px 0' }}
          />
        ) : items.length === 0 ? (
          // Reached only on a CONFIRMED successful response with zero
          // items (`status === 'success'`) — a genuinely empty Workspace/
          // tab/search result, never a failure (spec requirement
          // "只有 API 成功返回空列表时，才能显示真正的空项目状态").
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              debouncedQ
                ? t('contentProjects:emptyState.noResults')
                : teamEnabled && view === 'shared'
                  ? t('contentProjects:team.emptyShared')
                  : teamEnabled && view === 'all'
                    ? t('contentProjects:team.emptyAll')
                    : t('contentProjects:emptyState.noProjects')
            }
            style={{ padding: '48px 0' }}
          >
            {!debouncedQ && (!teamEnabled || view !== 'shared') ? (
              <Button type="primary" icon={<PlusOutlined />} loading={creating} onClick={() => void onCreate()}>
                {t('contentProjects:newProject')}
              </Button>
            ) : null}
          </Empty>
        ) : (
          <>
            <div className="cpg-gallery-grid">
              {items.map((item) => (
                <VisualProjectCard
                  key={item.id}
                  project={item}
                  copying={copyingId === item.id}
                  deleting={deletingId === item.id}
                  selected={selectedIds.includes(item.id)}
                  selectionActive={selectionActive}
                  teamEnabled={teamEnabled}
                  togglingVisibility={togglingId === item.id}
                  onToggleVisibility={() => onToggleVisibility(item)}
                  onOpen={() => openProject(item.id, item.is_mine !== false)}
                  onCopy={() => onCopy(item)}
                  onDelete={() => onDelete(item)}
                  onToggleSelect={(checked) => toggleSelect(item.id, checked)}
                />
              ))}
            </div>
            {total > PAGE_SIZE ? (
              <div className="cpg-pagination">
                <Pagination current={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} showSizeChanger={false} />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
